<template>
	<div id="cesium-viewer" @mouseup="onMouseUp">
		<div v-show="sawActive">
			<shape-authoring-toolbar
				ref="shapeAuthoringToolbar"
				:undo-enabled="
					!!(
						shapeAuthoringWorkflow.history.stack.length &&
						shapeAuthoringWorkflow.history.stack.length >
						shapeAuthoringWorkflow.history.index
					)
				"
				:redo-enabled="
					!!(
						shapeAuthoringWorkflow.history.stack.length &&
						shapeAuthoringWorkflow.history.index > 0
					)
				"
				:completable="shapeAuthoringWorkflowCompletable"
				:container="$el"
				@exit="onExitShapeAuthoringWorkflow"
				@undo="shapeAuthoringWorkflow.history.undo()"
				@redo="shapeAuthoringWorkflow.history.redo()"
			/>
			<shape-authoring-vertex
				v-for="(vertex, index) in shapeAuthoringWorkflow.screenVertices"
				:key="index"
				:style="`left:${vertex.x}px;top:${vertex.y}px`"
				:controls="index === shapeAuthoringWorkflow.selectedVertexId"
				:vertex="shapeAuthoringWorkflow.vertices[index]"
				:closeable="
					index === 0 &&
						!shapeAuthoringWorkflowLoopClosed &&
						shapeAuthoringWorkflow.vertices.length > 2
				"
				@mousedown="onShapeAuthoringVertexDragBegin(index)"
				@update="onShapeAuthoringVertexUpdate($event, index)"
				@delete="onShapeAuthoringVertexDelete(index)"
				@close-loop="onShapeAuthoringVertexCloseLoop"
			/>
			<shape-authoring-vertex
				v-for="(segment, index) in shapeAuthoringWorkflowSegments"
				:key="`spliiter-${index}-${index + 1}`"
				:style="`left:${segment.x}px;top:${segment.y}px`"
				splitter
				@mousedown="splitSegment(index)"
			/>
		</div>
		<div v-show="placementActive">
			<b-button
				variant="dark"
				style="position: absolute; top: 2rem; left: 50%; transform:translateX(-50%);z-index:10"
				@click="onExitPlacementWorkflow"
			>
				{{ $t('placement-workflow.exit') }}
			</b-button>
		</div>
		<div v-show="brightnessActive">
			<div
				ref="floatmenu"
				style="color: white; background: orange; position: absolute; top: 5rem; left: 50%; transform:translateX(-50%);z-index:10; width:50px"
			>
				TEST
			</div>

			<b-button
				style="color: black; background: orange; position: absolute; top: 14rem; left: 50%; transform:translateX(-50%);z-index:10; width:fit-content"
				@click="hidePlacementMarker"
			>
				Hide Placement Marker
			</b-button>
			<b-form-input
				id="range-1"
				v-model="brightness"
				variant="dark"
				style="position: absolute; top: 2rem; left: 50%; transform:translateX(-50%);z-index:10; width:30%"
				type="range"
				min="0"
				max="100"
				@input="setBrightness"
			/>
			<div style="position:relative; margin:0px;">
				<div ref="parentEl" class="utility-box-2 text-center">
					<movable class="modaltitle" target="parentEl">
						<label
							style="background-color:gray;color:white; cursor: pointer;"
							class="w-100 text-center m-0 p-0"
							for="model-scale"
							size="sm"
						>Model Editor</label>
					</movable>
					<span
						style="color:white;"
						class="w-100 text-center"
						size="sm"
					>{{ `${modelWorkflow.focusedName}` }}</span>

					<label
						style="background-color:black;color:white;"
						class="w-100 text-center"
						for="model-rotation"
						size="sm"
					>Rotation (degrees)</label>

					<b-form-input
						id="model-rotation"
						ref="modelrotation"
						v-model="modelWorkflow.rotation"
						style="text-align:center"
						size="sm"
						type="number"
						:min="0"
						:max="360"
						:step="1"
						:debounce="500"
						@change="throttleMethod(rotateModel)"
					/>
					<label
						style="background-color:black;color:white;"
						class="w-100 text-center"
						for="model-scale"
						size="sm"
					>Scale</label>
					<b-form-input
						id="model-scale"
						v-model="modelWorkflow.scale"
						style="text-align:center"
						size="sm"
						type="number"
						:min="0.001"
						:max="10"
						:step="0.01"
						:debounce="500"
						@change="throttleMethod(scaleModel)"
					/>
					<label
						style="background-color:black;color:white"
						class="w-100 text-center"
						for="model-height"
						size="sm"
					>Height Above Ground (meters)</label>

					<b-form-input
						id="model-height"
						v-model="modelWorkflow.height"
						style="text-align:center"
						size="sm"
						type="number"
						:step="1"
						:debounce="500"
						@change="throttleMethod(heightOfModel)"
					/>
					<b-input-group>
						<b-button
							class="w-50"
							variant="dark"
							@click="
								modelWorkflow.moveActive = !modelWorkflow.moveActive
							"
						>
							{{
								`${
									modelWorkflow.moveActive
										? 'Select Point'
										: 'Move model'
								}`
							}}
						</b-button>
						<b-button
							class="w-50"
							variant="dark"
							@click="
								toggleDebugWireframeModel(
									modelWorkflow.focusedId
								)
							"
						>
							Debug
						</b-button>
					</b-input-group>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	/* eslint-disable prefer-destructuring */
	/* eslint-disable guard-for-in */
	/* eslint-disable no-case-declarations */
	/* eslint-disable vue/attribute-hyphenation */
	/* eslint-disable array-callback-return */
	import {
		BoundingSphere,
		Camera,
		Cartesian3,
		Cartographic,
		Cesium3DTileStyle,
		ClassificationType,
		Color,
		createOsmBuildings,
		createWorldTerrain,
		Ellipsoid,
		GroundPrimitive,
		HeadingPitchRange,
		Ion,
		KeyboardEventModifier,
		Math as CesiumMath,
		Matrix4,
		ModelOutlineLoader,
		PrimitiveCollection,
		Rectangle,
		sampleTerrainMostDetailed,
		ScreenSpaceEventHandler,
		ScreenSpaceEventType,
		ShowGeometryInstanceAttribute,
		Viewer,
	} from 'cesium';
	import { arrayToTree } from 'performant-array-to-tree';
	import { mapGetters, mapMutations, mapState } from 'vuex';
	import {
		Style,
		createEntityFromPlainEntity,
		LineString,
		Polygon,
		MultiPolygon,
	} from '@/classes/Entity';
	import { HistoryStack } from '@/classes/History';
	import 'cesium/Build/Cesium/Widgets/widgets.css';
	import { polygon } from '@turf/helpers';
	import bbox from '@turf/bbox';
	import ShapeAuthoringToolbar from './ShapeAuthoringToolbar.vue';
	import ShapeAuthoringVertex from './ShapeAuthoringVertex.vue';
	import * as assetDatasetHandler from './assetDatasetHandler';
	import * as modelHandler from './modelHandler';
	import * as geoDataHandler from './geoDataHandler';
	import * as osmHandler from './osmHandler';
	import * as inputEventHandler from './inputEventHandler';
	import * as shapeAuthoringHandler from './shapeAuthoringHandler';

	const _debounce = require('lodash/debounce');
	const _throttle = require('lodash/throttle');
	// The following enums should really be symbols instead of strings but vues hot reloading can't handle that

	/** @enum Various states the viewer can be in */
	export const VIEWER_STATE = Object.freeze({
		/** The user can freely navigate the globe and select objects by clicking on them */
		DEFAULT: 'DEFAULT',
		/** Shape authoring workflow allowing the user to author a shape */
		SAW: 'SAW',
		/** Whenever a user clicks a "place" event is fired */
		PLACEMENT: 'PLACEMENT',
		/** OSM selection workflow for saving locations/camera views */
		OSM_SELECT: 'OSM_SELECT',
		ZOOM: 'ZOOM',
	});

	/** @enum Enumerates the shape types that can be requested to be authored by the user */
	export const AUTHORING_SHAPE = Object.freeze({
		PATH: 'path',
		POLYGON: 'polygon',
	});

	/**
	 * @typedef SAWSegment
	 * @member {Number} index The index of the segment correlates with the starting vertex index
	 * @member {Number} x The x position of the screenspace vertex
	 * @member {Number} y The y position of the screenspace vertex
	 */

	let cesiumViewer;
	const DEFAULT_STYLE = new Style();
	export default {
		name: 'CesiumViewer',
		components: {
			ShapeAuthoringToolbar,
			ShapeAuthoringVertex,
		},
		data() {
			return {
				postUpdate: 0,
				setLoading: 0,
				brightness: 70,
				brightnessActive: false,
				/**
				 * @member {VIEWER_STATE} CesiumViewer#state The currently active state
				 * @readonly
				 */
				state: VIEWER_STATE.DEFAULT,
				/**
				 * Shape authoring workflow related data
				 * @private
				 */
				shapeAuthoringWorkflow: {
					/** @member {String} shapeAuthoringWorkflow.shape The shape that's currently being authored as enumerated by {@linkcode AUTHORING_SHAPE} */
					shape: AUTHORING_SHAPE.PATH,
					/** @member {Cartesian3[]} shapeAuthoringWorkflow.vertices The vertices of the shape */
					vertices: [],
					/** @member {Cartesian2[]} shapeAuthoringWorkflow.screenVertices The window representations of the vertices */
					screenVertices: [],
					/** @member {HistoryStack} shapeAuthoringWorkflow.history Shape authoring history, cleared when the user exits the workflow */
					history: new HistoryStack(),
					/** @member {Number} shapeAuthoringWorkflow.draggedVertexId Used a state variable to identify currently dragged vertex */
					draggedVertexId: -1,
					/** @member {?Cartesian3} shapeAuthoringWorkflow.draggedVertexOrigin Used to cache the initial position of a dragged vertex so we can create a history item */
					draggedVertexOrigin: null,
					/** @member {Number} shapeAuthoringWorkflow.selectedVertexId Used a state variable to identify currently selected vertex */
					selectedVertexId: -1,
				},
				/**
				 * Placement workflow related data
				 * @private
				 */
				placementWorkflow: {
					/** @member {Cartesian3} placementWorkflow.positions Positions clicked during placement workflow */
					positions: [],
					/** @member {HistoryStack} placementWorkflow.history The history for the placement workflow */
					history: new HistoryStack(),
				},
				osmWorkflow: {
					addedIds: [],
					selectedIds: [],
					styledIds: {},
					focusedId: '',
					focusedEntity: '',
					colorConditions: [],
					showConditions: [],
					showOnlySelected: false,
					editMode: false,
				},
				modelWorkflow: {
					focusedId: null,
					focusedName: 'No 3D model selected...',
					position: { longitude: 0, latitude: 0, height: 0 },
					dimensions: { width: 0, depth: 0, height: 0 },
					center: { x: 0, y: 0, z: 0 },
					rotation: 1,
					pitch: 0,
					roll: 0,
					scale: 1,
					height: 0,
					moveActive: false,
					dragActive: false,
				},
				colors: {
					rgba: { r: 255, g: 255, b: 255, a: 0.4 },
				},
				debugClass: '',
				projectBoundary: {},
				dragMode: false,
				mouseEndPosition: { longitude: 0, latitude: 0, height: 0 },
				mouseDown: false,
				mouseWheel: 0,
				rectangleSelector: new Rectangle(),
				cartesian: new Cartesian3(),
				tempCartographic: new Cartographic(),
				firstPoint: new Cartographic(),
				firstPointSet: false,
			};
		},
		computed: {
			...mapState(['isLoading']),
			...mapGetters(['activeTour', 'currentTour']),

			/**
			 * @member {Boolean} sawActive true if viewer is in saw mode (we cant access the enum so we need to use this as a crutch)
			 * @private
			 * @readonly
			 */
			sawActive() {
				return this.state === VIEWER_STATE.SAW;
			},
			/**
			 * @member {Boolean} placementActive true if viewer is in placement mode (we cant access the enum so we need to use this as a crutch)
			 * @private
			 * @readonly
			 */
			placementActive() {
				return this.state === VIEWER_STATE.PLACEMENT;
			},
			/**
			 * @member {Boolean} osmSelectActive true if viewer is in your mode (we cant access the enum so we need to use this as a crutch)
			 * @private
			 * @readonly
			 */
			osmSelectActive() {
				return this.state === VIEWER_STATE.OSM_SELECT;
			},
			/**
			 * @member {Boolean} shapeAuthoringWorkflowCompletable true if the shape has a functional amount of vertices
			 * @private
			 */
			shapeAuthoringWorkflowCompletable() {
				if (this.shapeAuthoringWorkflow.shape === AUTHORING_SHAPE.PATH)
					return this.shapeAuthoringWorkflow.vertices.length > 1;
				if (
					this.shapeAuthoringWorkflow.shape ===
					AUTHORING_SHAPE.POLYGON
				)
					return this.shapeAuthoringWorkflow.vertices.length > 2;
				return true;
			},
			/**
			 * @member {Boolean} shapeAuthoringWorkflowLoopClosed true if the loop was closed by the user
			 * @private
			 */
			shapeAuthoringWorkflowLoopClosed() {
				return false;
				/* return (
						this.shapeAuthoringWorkflow.vertices.length > 1 &&
						this.shapeAuthoringWorkflow.vertices[0] ===
							this.shapeAuthoringWorkflow.vertices[this.shapeAuthoringWorkflow.vertices.length - 1]
					); */
			},
			/**
			 * @member {SAWSegment[]} shapeAuthoringWorkflowSegments
			 * @private
			 */
			shapeAuthoringWorkflowSegments: {
				cache: false,
				get() {
					const ret = [];
					for (
						let i = 0;
						i <
						this.shapeAuthoringWorkflow.screenVertices.length - 1;
						i++
					) {
						const v0 = this.shapeAuthoringWorkflow.screenVertices[
							i
						];
						const v1 = this.shapeAuthoringWorkflow.screenVertices[
							i + 1
						];
						const dx = v1.x - v0.x;
						const dy = v1.y - v0.y;
						ret.push({
							index: i,
							x: v0.x + dx * 0.5,
							y: v0.y + dy * 0.5,
						});
					}
					return ret;
				},
			},
			showOpenStreetMapsBuildings: {
				get() {
					return cesiumViewer.scene.primitives.contains(
						this.osmBuildings
					);
				},
				set(v) {
					if (v === this.showOpenStreetMapsBuildings) return;
					if (v) cesiumViewer.scene.primitives.add(this.osmBuildings);
					else
						cesiumViewer.scene.primitives.remove(this.osmBuildings);
				},
			},
			osmBuildingsStyleConditions() {
				// @TODO: refactor here for better time complexity looping over addedIds
				const show = {
					conditions: this.osmWorkflow.addedIds
						.map((id) => [
							`\${elementId} === ${id.elementId}`,
							id.show,
						])
						.concat([[true, !this.osmWorkflow.showOnlySelected]]),
				};

				const added = this.osmWorkflow.addedIds.map((x) => [
					`\${elementId} === ${x.elementId}`,
					`${x.color}`,
				]);

				const selected = this.osmWorkflow.selectedIds.map((id) => [
					`\${elementId} === ${id.elementId}`,
					"color('blue', 0.4)",
				]);
				const color = {
					conditions: added
						.concat(selected)
						.concat([['true', "color('#d5d5ff', 1)"]]),
				};

				return { show, color };
			},
		},
		watch: {
			osmWorkflow: {
				// Will fire as soon as the component is created
				deep: true,
				handler(newval) {
					if (!newval.selectedIds.length) {
						this.$store.commit('setBuildingsSelected', false);
						if (document.body.style.cursor === 'wait') {
							document.body.style.cursor = 'default';
						}
					}
					try {
						this.osmBuildings.style = new Cesium3DTileStyle(
							this.osmBuildingsStyleConditions
						);
					} catch (error) {
						/*  */
					}
				},
			},
		},
		created() {
			this.$_linearEntities = [];
			this.$_linearEntitiesState = {};
			this.$_tags = [];
		},
		async mounted() {
			window.CESIUM_BASE_URL = '';

			const viewerOptions = {
				/* imageryProvider: new MapboxImageryProvider({
						mapId: 'mapbox.satellite',
						accessToken: this.$store.state.config.apiTokens.mapbox,
					}), */
				baseLayerPicker: true,
				animation: false,
				fullscreenButton: false,
				geocoder: false,
				homeButton: false,
				infoBox: false,
				sceneModePicker: false,
				selectionIndicator: false,
				timeline: false,
				navigationHelpButton: false,
				navigationInstructionsInitiallyVisible: false,
				/* scene3DOnly: true, */
				shouldAnimate: false,
				maximumRenderTimeChange: Infinity,
				requestRenderMode: true,
				skyBox: false,
			};

			// Use cesium ION as terrain provier when credentials are present
			if (this.$store.state.config.apiTokens.cesiumIon) {
				Ion.defaultAccessToken = this.$store.state.config.apiTokens.cesiumIon;
				viewerOptions.terrainProvider = createWorldTerrain({
					requestWaterMask: false,
				});
			}

			await this.$nextTick();

			cesiumViewer = new Viewer('cesium-viewer', viewerOptions);
			cesiumViewer.scene.globe.tileCacheSize = 2000;
			cesiumViewer.scene.globe.maximumScreenSpaceError = 4;

			const czm = this;

			cesiumViewer.scene.preRender.addEventListener(() => {
				czm.postUpdate = 0;
				
				if (!czm.$store.state.isRendering && (this.state !== VIEWER_STATE.SAW))
					czm.$store.state.isRendering = true;
				if (this.dragMode) czm.$store.state.isRendering = false;
			});

			cesiumViewer.scene.postUpdate.addEventListener(() => {
				if (czm.postUpdate >= 0) {
					czm.postUpdate += 1;
				}
				if (czm.postUpdate === 60) {
					// we've run @60FPS for 60 frames [1000ms] so we're *probably* done loading
					czm.postUpdate = -1;
					czm.$store.state.isRendering = false;
					if (czm.$store.state.isLoading)
						czm.$store.state.isLoading = false;
				}
			});

			// the following two lines disable outlines, this is a workaround until the following issue is fixed
			// https://github.com/CesiumGS/cesium/issues/8959
			const originalOutlineExtensionHandler =
				ModelOutlineLoader.hasExtension;
			ModelOutlineLoader.hasExtension = () => false;

			// reenable outlines
			// ModelOutlineLoader.hasExtension = originalOutlineExtensionHandler;
			cesiumViewer.imageryLayers.get(0).brightness = 0.9;
			this.cesiumEventHandler = new ScreenSpaceEventHandler(
				cesiumViewer.canvas
			);

			/* Input Actions in inputEventHandler.js */

			// Left-button input actions
			this.cesiumEventHandler.setInputAction((click) => {
				this.shapeAuthoringWorkflow.selector.show = false;
				switch (this.state) {
					case VIEWER_STATE.DEFAULT: {
						this.leftClickDefault(click);
						break;
					}
					case VIEWER_STATE.OSM_SELECT: {
						this.leftClickOsmSelect(click);
						break;
					}
					case VIEWER_STATE.SAW: {
						this.leftClickSaw(click);
						break;
					}
					case VIEWER_STATE.PLACEMENT: {
						this.leftClickPlacement(click);
						break;
					}
				}
			}, ScreenSpaceEventType.LEFT_CLICK);

			this.cesiumEventHandler.setInputAction(() => {
				this.mouseDown = true;
			}, ScreenSpaceEventType.LEFT_DOWN);

			this.cesiumEventHandler.setInputAction(() => {
				this.mouseDown = false;
			}, ScreenSpaceEventType.LEFT_UP);

			// Right-button input actions
			this.cesiumEventHandler.setInputAction((rightClick) => {
				this.rightClickDefault(rightClick);
			}, ScreenSpaceEventType.RIGHT_CLICK);

			// Wheel input actions
			this.cesiumEventHandler.setInputAction((wheel) => {
				const wheelBoolean = Boolean(wheel > 0);
				if (this.dragMode) {
					cesiumViewer.scene.screenSpaceCameraController.enableZoom = false;
					const number = wheelBoolean
						? Math.abs(++this.mouseWheel % 360)
						: Math.abs(--this.mouseWheel % 360);
					this.modelWorkflow.rotation = number;
					this.$refs.modelrotation.$emit('change');
				}
			}, ScreenSpaceEventType.WHEEL);

			// Mouse movement input actions
			this.cesiumEventHandler.setInputAction((mouseMove) => {
				this.mouseMoveDefault(mouseMove);
				switch (this.state) {
					case VIEWER_STATE.SAW: {
						this.mouseMoveSaw(mouseMove);
						break;
					}
				}
			}, ScreenSpaceEventType.MOUSE_MOVE);

			this.cesiumEventHandler.setInputAction(
				(movement) => {
					this.mouseMoveShift(movement);
				},
				ScreenSpaceEventType.MOUSE_MOVE,
				KeyboardEventModifier.SHIFT
			);

			this.cesiumEventHandler.setInputAction(
				() => {
					this.leftDownShift();
				},
				ScreenSpaceEventType.LEFT_DOWN,
				KeyboardEventModifier.SHIFT
			);

			this.cesiumEventHandler.setInputAction(
				() => {
					this.leftUpShift();
				},
				ScreenSpaceEventType.LEFT_UP,
				KeyboardEventModifier.SHIFT
			);

			this.cesiumEventHandler.setInputAction(() => {
				this.leftUp();
			}, ScreenSpaceEventType.LEFT_UP);

			/* Create reusable Cesium data structures - shapeAuthoringHandler.js */
			this.createShapeAuthoringEntities();

			cesiumViewer.camera.percentageChanged = 0.001;
			cesiumViewer.camera.changed.addEventListener(
				this.updateShapeAuthoringVertices
			);
		},
		destroyed() {
			try {
				this.cesiumEventHandler.destroy();
				cesiumViewer.camera.changed.removeEventListener(
					this.cameraEventListener,
					true
				);
			} catch (error) {
				/*  */
			}
		},
		methods: {
			...mapMutations([
				'updateTourViewProperties',
				'setElementEditorEntityDataId',
			]),
			debounceMethod: _debounce((fn) => {
				fn();
			}, 300),
			throttleMethod: _throttle((fn) => {
				fn();
			}, 150),
			...inputEventHandler,
			/* Methods to load/save/manipulate 2D geo data files (geojson, topojson, kml) */
			...geoDataHandler,
			/* Methods to load/save/manipulate imported 3D OBJ file that are converted to GLTF & Ion 3D models */
			/* @TODO: add interface for Ion resources */
			...modelHandler,
			/* Methods to save/style buildings from OpenStreetMaps */
			...osmHandler,
			/* Shape Authoring Workflow Methods */
			...shapeAuthoringHandler,
			/* Asset Dataset Creation Method */
			...assetDatasetHandler,
			/**
			 * Load entities and set homeview to the center of all of them, note that we don't use a prop for this data as we don't want reactivity wrappers on everything
			 * @param {Object[]} scenarioData
			 */
			async loadScenarioData(scenarioData) {
				this.$_linearEntities = [];
				this.$_tags = [];
				this.osmWorkflow.addedIds = [];
				this.osmWorkflow.selectedIds = [];
				this.osmWorkflow.focusedEntity = '';
				this.osmWorkflow.focusedId = '';

				const plainEntities = scenarioData.entities;
				this.addScenarioData(plainEntities).then((x) => {
					this.zoomHome();
				});
				// Add the logged instance of this component as a global variable in your browser console for debugging
				window.czm = this;
			},
			async addScenarioData(plainEntities) {
				const linearEntities = [];
				const lineGeometries = [];
				const multiPolygonGeometryTypes = [];
				const polygonGeometryTypes = {};
				const primitiveCollections = {};

				const makeChildPrimitive = (geometryInstance) => {
					// geometryInstance ids are objects

					const childPrimitive = new GroundPrimitive({
						geometryInstances: geometryInstance,
						appearance: Polygon.APPEARANCE,
						asynchronous: false,
						classificationType: ClassificationType.TERRAIN,
					});
					childPrimitive.id = geometryInstance.id;
					primitiveCollections[
						linearEntities[geometryInstance.id].parent.id
					].add(childPrimitive);
				};

				for (const entityDescr of plainEntities) {
					this.$_linearEntitiesState[entityDescr.id] = {
						show: true,
						color: '',
					};

					try {
						linearEntities[entityDescr.id] = linearEntities[
							linearEntities.length
						] = createEntityFromPlainEntity(entityDescr);
					} catch (e) {
						// console.log(e.message);
						// @TODO: handle invalid entities
					}
				}
				for (const entityDescr of plainEntities) {
					try {
						if (entityDescr.parent) {
							linearEntities[entityDescr.id].parent =
								linearEntities[entityDescr.parent];
						}
					} catch (error) {
						console.error(error, entityDescr);
					}
				}
				for (const entity of linearEntities) {
					entity.updateGeometry();
					if (entity.type === 'layer') {
						if (!primitiveCollections[entity.id])
							primitiveCollections[
								entity.id
							] = new PrimitiveCollection();

						primitiveCollections[entity.id].id = entity.id;
					}
					if (entity.type === 'file') {
						if (entity.data[0].data.type === 'json') {
							this.loadGeo(entity.id, entity.data[0].data);
						}
						if (entity.data[0].data.type === 'model') {
							this.loadGltf(entity.id, entity.data[0].data);
						}
					}
					if (entity.type === 'osmBuildingModel') {
						this.osmWorkflow.addedIds.push(
							JSON.parse(JSON.stringify(entity.data[0].data))
						);
					}
					if (entity instanceof LineString) {
						lineGeometries.push(entity.geometryInstances[0]);
					}
					if (entity instanceof Polygon && entity.type !== 'file') {
						if (!polygonGeometryTypes[entity.key])
							polygonGeometryTypes[entity.key] = [];
						polygonGeometryTypes[entity.key].push(
							entity.geometryInstances[1]
						);
					}

					if (entity instanceof MultiPolygon) {
						multiPolygonGeometryTypes.push(
							entity.geometryInstances[0]
						);
					}
				}

				for (const multiPolygonGeometry of multiPolygonGeometryTypes) {
					makeChildPrimitive(multiPolygonGeometry);
				}

				// eslint-disable-next-line
				for (const renderKey in polygonGeometryTypes) {
					// @TODO: compensate for entities thta are a top level parent but not a 'layer' - it is not added to a collection (ala the former Streetlight custom entity)
					if (polygonGeometryTypes[renderKey].length) {
						for (const geometryInstance of polygonGeometryTypes[
							renderKey
						]) {
							makeChildPrimitive(geometryInstance);
						}
					}
				}

				// @TODO: Put LineString geometries in their proper PrimitiveCollection
				/* cesiumViewer.scene.primitives.add(
						new GroundPolylinePrimitive({
							geometryInstances: lineGeometries,
							appearance: LineString.APPEARANCE,
							asynchronous: false,
							classificationType: ClassificationType.TERRAIN,
						})
				); */
				await GroundPrimitive.initializeTerrainHeights();
				// eslint-disable-next-line guard-for-in
				for (const primitiveCollection in primitiveCollections) {
					cesiumViewer.scene.primitives.add(
						primitiveCollections[primitiveCollection]
					);
				}
				// set project boundary to extent defined on project object
				this.projectBoundary = this.constructRectangleFromDegrees(
					bbox(
						polygon(this.$store.state.project.boundary.coordinates)
					)
				);
				await this.getTreeMenu();
			},
			async updateEntityData(id, newData) {
				if (!this.$_linearEntities[id]) return;
				const currentData = this.$_linearEntities[id];
				const writable = ['name', 'description', 'data'];
				const postBody = {};
				const newDescription =
					newData.description !== currentData.description;
				const newName =
					newData.name && newData.name !== currentData.name;

				for (const property in newData) {
					if (writable.includes(property)) {
						switch (property) {
							case 'name':
								if (newName) postBody.name = newData.name;
								break;
							case 'description':
								if (newDescription)
									postBody.description = newData.description;
								break;
							case 'data':
								postBody.data = [
									{
										type: currentData.data[0].type,
										basedata: true,
										subType: null,
										data: {
											...currentData.data[0].data,
											...newData.data,
										},
									},
								];
								break;
						}
					}
				}

				const patched = await this.axios.patch(
					`/scenarios/${this.$store.state.scenario}/entities/${id}`,
					postBody
				);

				if (newName) {
					this.$root.$emit('patch-menu', patched.data);
				}

				currentData.data[0].data = patched.data.data[0].data;
				currentData.name = patched.data.name;
				currentData.description = patched.data.description;

				this.$root.$emit('new-activity');
			},
			async deleteEntity(entity) {
				const selectedEntity = this.$_linearEntities[entity];
				const isParentPrimitive = !selectedEntity.parent;
				const childEntities = this.getChildren(entity);
				if (
					selectedEntity.data[0].data.type &&
					selectedEntity.data[0].data.type.match('json')
				) {
					// eslint-disable-next-line consistent-return
					const filterFunc = (x) => {
						let id;
						if (x.layer && x.layer === entity) {
							id = x.id;
						}
						return id;
					};
					const layerIds = cesiumViewer.entities.values
						.filter(filterFunc)
						.filter((x) => x);
					layerIds.map((x) => {
						return cesiumViewer.entities.removeById(x.id);
					});
					await this.axios.delete(
						`/scenarios/${
							this.$store.state.scenario
						}/entities?ids=${[entity]}`
					);
				} else {
					const osmData = selectedEntity.data[0].data.properties
						? selectedEntity.data[0].data.properties.elementId
						: null;
					if (osmData)
						this.osmWorkflow.addedIds = this.osmWorkflow.addedIds.filter(
							(x) => x.elementId !== osmData
						);
					this.updateOsmStyle();
					if (isParentPrimitive) {
						const ids = [selectedEntity, ...childEntities].map(
							(x) => x.id
						);
						await this.axios.delete(
							`/scenarios/${this.$store.state.scenario}/entities?ids=${ids}`
						);
					}
					this.getTreeMenu();
					for (
						let i = 0;
						i < cesiumViewer.scene.primitives.length;
						++i
					) {
						const currentPrimitive = cesiumViewer.scene.primitives.get(
							i
						);

						for (let z = 0; z < currentPrimitive.length; ++z) {
							const collectionPrimitive = currentPrimitive.get(z);
							if (collectionPrimitive.id === entity) {
								currentPrimitive.remove(collectionPrimitive);
								collectionPrimitive.destroy();
							} else if (
								collectionPrimitive.id &&
								collectionPrimitive.id.match(selectedEntity.id)
							) {
								currentPrimitive.remove(collectionPrimitive);
								collectionPrimitive.destroy();
							}
						}
						if (
							isParentPrimitive &&
							currentPrimitive.id === entity
						) {
							cesiumViewer.scene.primitives.removeAndDestroy(
								currentPrimitive
							);
						}
					}
				}
				cesiumViewer.scene.requestRender();
			},
			async getTreeMenu() {
				const scenarioResponse = await this.axios.get(
					`/scenarios/${this.$store.state.scenario}/entities?nogeo=true`
				);
				const linearEntities = [];
				for (const entityDescr of scenarioResponse.data) {
					this.$_linearEntitiesState[entityDescr.id] = {
						show: true,
						color: '',
					};

					try {
						linearEntities[entityDescr.id] = linearEntities[
							linearEntities.length
						] = entityDescr;
					} catch (e) {
						// console.log(e.message);
						// @TODO: handle invalid entities
					}
				}

				for (const entityDescr of scenarioResponse.data) {
					try {
						if (entityDescr.parent) {
							linearEntities[entityDescr.id].parent =
								linearEntities[entityDescr.parent].id;
						}
					} catch (error) {
						console.error(error, entityDescr);
					}
				}
				this.$_linearEntities = linearEntities;
				const nestedEntities = arrayToTree(scenarioResponse.data, {
					dataField: null,
					parentId: 'parent',
				});
				this.$root.$emit('menu-message', nestedEntities);
			},
			async importFile(uri) {
				let metadata;
				if (uri.type.match('model')) {
					metadata = await this.importModelFile(uri);
				} else {
					metadata = await this.importGeoFile(uri);
				}
				this.addFileEntity(metadata);
			},
			async addFileEntity(metadata) {
				const { geometry } = metadata;
				const data = { ...metadata };
				delete data.geometry;
				const name =
					metadata.name.substring(
						0,
						metadata.name.lastIndexOf('.')
					) || metadata.name;
				const entityFormat = {
					name,
					type: 'file',
					geometry,
					tags: ['imported'],
					created_at: new Date(),
					updated_at: new Date(),
					data: [
						{
							type: 'generic',
							basedata: true,
							subType: null,
							data,
						},
					],
				};
				const postFileEntityData = await this.axios.post(
					`/scenarios/${this.$store.state.scenario}/entities`,
					entityFormat
				);

				await this.createAssetDataset(postFileEntityData.data.id, {
					name,
				});

				await this.addScenarioData([postFileEntityData.data]);
			},
			getCesiumData() {
				return {
					viewer: cesiumViewer,
					scene: cesiumViewer.scene,
					entities: cesiumViewer.entities,
					primitives: cesiumViewer.scene.primitives,
				};
			},
			getPrimitiveById(id) {
				const primitive = Array.from(
					Array(cesiumViewer.scene.primitives.length),
					(_, index) => index
				)
					.map((x) => cesiumViewer.scene.primitives.get(x))
					.find((y) => y.id === id);
				return primitive;
			},

			/**
			 * Convert a geographic bounding box to a Cesium rectangle.
			 * @param {Array} [coords] Array of geographic coordinates/degrees (west, south, east, north)
			 */
			constructRectangleFromDegrees(coords) {
				if (Array.isArray(coords) && coords.length === 4) {
					// eslint-disable-next-line new-cap
					const rec = new Rectangle.fromDegrees(...coords);
					return rec;
				}
				return null;
			},
			/**
			 * convert an array of Cartesian3 points to Geographic coordinates and
			 * assemble to a polygon
			 * @param Cartesian3[] array of Cartesian3 points representing the polygon
			 */
			constructGeoPolyFromCartesian(pointArray) {
				if (
					!(
						pointArray &&
						Array.isArray(pointArray) &&
						pointArray.length >= 3
					)
				)
					return false;
				const geoPolygon = [];
				for (const srcpt of pointArray) {
					const cpt = Cartographic.fromCartesian(srcpt);
					geoPolygon.push([
						CesiumMath.toDegrees(cpt.longitude),
						CesiumMath.toDegrees(cpt.latitude),
					]);
				}
				// check the last point added; if not matching the first, append the first to the end.
				const lasti = geoPolygon.length - 1;
				if (
					!(
						geoPolygon[0][0] === geoPolygon[lasti][0] &&
						geoPolygon[0][1] === geoPolygon[lasti][1]
					)
				)
					geoPolygon.push(geoPolygon[0]);
				return geoPolygon;
			},
			/**
			 * Move camera to a given location over a given amount of time, see [Camera#flyTo](https://cesium.com/learn/cesiumjs/ref-doc/Camera.html?classFilter=camera)
			 * @param {Cartesian3|Rectangle} [what] The location to fly to, null flies to the homeview
			 * @param {Number} [duration=0.1] duration in seconds
			 */
			async focus(what, duration = 0.1) {
				if (!what) cesiumViewer.camera.flyHome(duration);
				else cesiumViewer.camera.flyTo({ destination: what, duration });
				await new Promise((resolve) =>
					setTimeout(resolve, duration * 1000)
				);
			},
			zoomBack() {
				this.focus(Camera.DEFAULT_VIEW_RECTANGLE);
			},
			zoomHome() {
				Camera.DEFAULT_VIEW_FACTOR = 0.0005;
				this.focus(this.projectBoundary);
			},
			toggleBrightnessActive() {
				this.brightnessActive = !this.brightnessActive;
			},
			setBrightness() {
				cesiumViewer.imageryLayers.get(0).brightness =
					this.brightness / 100;
				cesiumViewer.scene.requestRender();
			},
			setFocusedEntity(id) {
				const entity = this.$_linearEntities[id];
				let model;

				const isEntity = !!cesiumViewer.entities.values.find(
					(x) => x.layer && x.layer === entity.id
				);
				if (isEntity) {
					cesiumViewer.entities.values.filter(
						(x) => x.layer && x.layer === entity.id
					);
				}
				if (entity.tags.includes('osm')) {
					if (entity.type === 'layer') {
						model = this.$_linearEntities.find(
							(x) =>
								x.type === 'osmBuildingModel' && x.parent === id
						);
					}
					if (entity.type === 'osmBuildingModel') {
						model = entity;
					}
					if (model.id === this.osmWorkflow.focusedEntity) return;
					if (model) {
						this.osmWorkflow.focusedEntity = model.id;
						this.osmWorkflow.focusedId =
							model.data[0].data.elementId;
						if (model.data[0].data.color.match('rgba')) {
							const colors = model.data[0].data.color
								.replace(/rgba\((.*?)\)/, '$1')
								.split(',');
							const rgba = {
								r: colors[0],
								g: colors[1],
								b: colors[2],
								a: colors[3],
							};
							this.colors = { rgba };
						} else {
							this.colors = {
								rgba: { r: 255, g: 213, b: 213, a: 1 },
							};
						}
					}
				}
				// eslint-disable-next-line consistent-return
			},
			getDisplayData(id) {
				return this.$_linearEntities[id].data[0];
			},
			getTopLevelParentId(input) {
				const parents = [input];
				const findParents = (entityId) => {
					const entity = this.$_linearEntities[entityId];
					if (entity && entity.parent) {
						const id = entity.parent.id || entity.parent;
						parents[parents.length] = id;
						findParents(id);
					}
				};
				findParents(input);
				return parents.pop();
			},
			getChildren(entity) {
				return this.$_linearEntities.filter((x) => x.parent === entity);
			},
			/**
			 * Set whether or not an entity is visible on the canvas.
			 * @param {String} entity DMP ID of the entity
			 * @param {Boolean} isVisible Whether or not the entity should be visible
			 */
			async setEntityVisibility(entity, value) {
				this.setEntityAttribute(entity, 'show', value);
			},
			async setEntityColor(entity, value) {
				// @TODO: check/convert developer input for valid color; fail gracefully
				this.setEntityAttribute(entity, 'color', value);
			},
			// @TODO: add comments
			async setEntityAttribute(
				entity,
				attribute,
				value,
				filterProperty = null
			) {
				const selectedEntity = this.$_linearEntities[entity];

				try {
					const isParentPrimitive = !selectedEntity.parent;
					const topLevelParent = this.getTopLevelParentId(entity);
					this.$_linearEntitiesState[entity][attribute] = value;

					const geometryInstanceAttributeValue =
						attribute === 'show'
							? ShowGeometryInstanceAttribute.toValue(value)
							: value;
					const geometryInstance = (primitive) =>
						primitive.getGeometryInstanceAttributes &&
						primitive.getGeometryInstanceAttributes(
							selectedEntity.id
						);
					if (selectedEntity.type === 'file') {
						if (attribute === 'color') {
							const {
								r: red,
								g: green,
								b: blue,
								a: alpha,
							} = value.rgba;
							const collectedProperties =
								selectedEntity.data[0].data.properties;

							const geometryTypes = [
								'billboard',
								'box',
								'corridor',
								'cylinder',
								'ellipse',
								'ellipsoid',
								'path',
								'plane',
								'point',
								'polygon',
								'polyline',
								'polylineVolume',
								'rectangle',
								'wall',
							];
							if (
								cesiumViewer.entities.values.find(
									(x) => x.layer === entity
								)
							) {
								if (filterProperty) {
									cesiumViewer.entities.values
										.filter((x) => x.layer === entity)
										.filter(
											(y) =>
												y.properties &&
												y.properties[
													filterProperty.filter
												] &&
												String(
													y.properties[
														filterProperty.filter
													]
												) ===
													String(
														filterProperty.property
													)
										)
										.forEach((y) => {
											const geometryType = geometryTypes
												.map((types) =>
													y[types] ? types : undefined
												)
												.filter((type) => type)[0];
											y[
												geometryType
											].material.color.setValue(
												new Color(
													red / 255,
													green / 255,
													blue / 255,
													alpha
												)
											);
										});
									collectedProperties.forEach((property) => {
										if (
											String(
												property[filterProperty.filter]
											) ===
											String(filterProperty.property)
										) {
											property.color = [
												red,
												green,
												blue,
												alpha * 255,
											].toString();
										}
									});
								} else {
									cesiumViewer.entities.values
										.filter((x) => x.layer === entity)
										.forEach((y) => {
											const geometryType = geometryTypes
												.map((types) =>
													y[types] ? types : undefined
												)
												.filter((type) => type)[0];
											y[
												geometryType
											].material.color.setValue(
												new Color(
													red / 255,
													green / 255,
													blue / 255,
													alpha
												)
											);
										});
									collectedProperties.forEach((property) => {
										property.color = [
											red,
											green,
											blue,
											alpha * 255,
										].toString();
									});
								}
								await this.updateEntityData(entity, {
									data: selectedEntity.data[0].data,
								});
							} else {
								for (
									let i = 0;
									i < cesiumViewer.scene.primitives.length;
									++i
								) {
									const currentPrimitive = cesiumViewer.scene.primitives.get(
										i
									);
									if (currentPrimitive.id === entity) {
										currentPrimitive.show = value;
									}
								}
							}
						}
						if (attribute === 'show') {
							if (
								cesiumViewer.entities.values.find(
									(x) => x.layer === entity
								)
							) {
								if (filterProperty) {
									cesiumViewer.entities.values
										.filter((x) => x.layer === entity)

										.forEach((x) => {
											const propertyValue =
												String(
													x.properties[
														filterProperty.filter
													].valueOf()
												) ===
												String(filterProperty.property);

											if (propertyValue) x.show = value;
										});
								} else {
									cesiumViewer.entities.values
										.filter((x) => x.layer === entity)
										.forEach((y) => {
											y.show = value;
										});
								}
							} else {
								for (
									let i = 0;
									i < cesiumViewer.scene.primitives.length;
									++i
								) {
									const currentPrimitive = cesiumViewer.scene.primitives.get(
										i
									);
									if (currentPrimitive.id === entity) {
										currentPrimitive.show = value;
									}
								}
							}
						}
					}

					if (
						selectedEntity.type === 'osmBuildingModel' ||
						selectedEntity.name === 'OSM Buildings'
					) {
						if (selectedEntity.name === 'OSM Buildings') {
							if (attribute === 'show') {
								this.osmWorkflow.showOnlySelected = !value;
							}
						}
						if (selectedEntity.type === 'osmBuildingModel') {
							const index = this.osmWorkflow.addedIds.find(
								(x) =>
									x.elementId ===
									selectedEntity.data[0].data.elementId
							);
							if (index && attribute === 'show') {
								index.show = value;
							}
							if (index && attribute === 'color') {
								this.colorOsmBuildingById(value);
							}
						}
						await this.updateOsmStyle();
						this.postOsmStyle();
					}
					for (
						let i = 0;
						i < cesiumViewer.scene.primitives.length;
						++i
					) {
						const currentPrimitive = cesiumViewer.scene.primitives.get(
							i
						);
						if (isParentPrimitive) {
							const primitiveGeometryInstance = geometryInstance(
								currentPrimitive
							);

							if (
								selectedEntity.type === 'layer' ||
								selectedEntity.type === 'osmBuildingModel'
							) {
								this.getChildren(selectedEntity.id).map((x) =>
									this.setEntityAttribute(
										x.id,
										attribute,
										value
									)
								);
							} else if (primitiveGeometryInstance) {
								primitiveGeometryInstance[
									attribute
								] = geometryInstanceAttributeValue;
							}
						} else if (
							currentPrimitive.id &&
							currentPrimitive.id === topLevelParent
						) {
							for (let z = 0; z < currentPrimitive.length; ++z) {
								const collectionPrimitive = currentPrimitive.get(
									z
								);
								const collectionPrimitiveGeometryInstance = geometryInstance(
									collectionPrimitive
								);
								if (collectionPrimitiveGeometryInstance) {
									collectionPrimitiveGeometryInstance[
										attribute
									] = geometryInstanceAttributeValue;
								} else if (
									collectionPrimitive.id &&
									collectionPrimitive.id.match(
										selectedEntity.id
									)
								) {
									collectionPrimitive[attribute] = value;
								}
							}
						}
						cesiumViewer.scene.requestRender();
					}
				} catch (error) {
					/*  */
				}
			},
			getEntityBoundingSphere(entity) {
				const selectedEntity = this.$_linearEntities[entity];
				const isParentPrimitive = !selectedEntity.parent;
				const topLevelParent = this.getTopLevelParentId(entity);
				let returnSphere;
				const boundingSphere = (primitive) => {
					let bSphere;
					if (primitive.getBoundingSphere) {
						bSphere = primitive.getBoundingSphere(
							selectedEntity.id
						);
					}
					if (primitive.boundingSphere) {
						bSphere = primitive.boundingSphere;
					}
					return bSphere;
				};

				try {
					if (
						selectedEntity.type.match('osm') ||
						(selectedEntity.tags.includes('osm') &&
							selectedEntity.type === 'layer')
					) {
						returnSphere = BoundingSphere.fromPoints([
							Cartesian3.fromDegrees(
								selectedEntity.data[0].data.properties[
									'cesium#longitude'
								],
								selectedEntity.data[0].data.properties[
									'cesium#latitude'
								],
								0
							),
						]);
					} else
						for (
							let i = 0;
							i < cesiumViewer.scene.primitives.length;
							++i
						) {
							const currentPrimitive = cesiumViewer.scene.primitives.get(
								i
							);
							if (currentPrimitive.id === entity) {
								const primitiveBoundingSphere = boundingSphere(
									currentPrimitive
								);

								if (primitiveBoundingSphere) {
									returnSphere = primitiveBoundingSphere;
								}
							}
							if (isParentPrimitive) {
								const primitiveBoundingSphere = boundingSphere(
									currentPrimitive
								);

								if (primitiveBoundingSphere) {
									returnSphere = primitiveBoundingSphere;
								}
							} else if (
								currentPrimitive.id &&
								currentPrimitive.id === topLevelParent
							) {
								for (
									let z = 0;
									z < currentPrimitive.length;
									++z
								) {
									const collectionPrimitive = currentPrimitive.get(
										z
									);

									if (collectionPrimitive.id === entity) {
										const collectionPrimitiveBoundingSphere = boundingSphere(
											collectionPrimitive
										);
										returnSphere = collectionPrimitiveBoundingSphere;
									}
								}
							}
						}
				} catch (error) {
					/*  */
				}
				return returnSphere;
			},
			/**
			 * Random swooping fly to an entity
			 * @param {BoundingSphere}
			 */
			doFly(boundingSphere, fly = false) {
				const heading =
					Math.floor(Math.random() * (50.49 - 40.49 + 1)) + 40.49;
				if (fly) {
					cesiumViewer.camera.flyToBoundingSphere(boundingSphere, {
						offset: {
							heading,
							pitch: -0.74109144709378514,
							range: 400,
						},
						duration: 0.5,
						complete: () => {
							cesiumViewer.camera.flyToBoundingSphere(
								boundingSphere,
								{
									offset: {
										heading,
										pitch: -0.74109144709378514,
										range: 200,
									},
									duration: 0.5,
								}
							);
						},
					});
				}

				cesiumViewer.camera.flyToBoundingSphere(boundingSphere, {
					offset: {
						heading,
						pitch: -0.74109144709378514,
						range: 200,
					},
					duration: 0,
				});
			},
			/**
			 * Zoom to an entity
			 * @param {String} entity DMP ID of the entity
			 */
			// @TODO: FIX ZOOM FOR ALL ENTITIES NOW THAT FILE STORAGE HAS CHANGED
			onZoom(entity) {
				try {
					const selectedEntity = this.$_linearEntities[entity];

					if (!selectedEntity) return;
					if (selectedEntity.data[0].data.type) {
						if (selectedEntity.data[0].data.type.match('json')) {
							const findEntity = cesiumViewer.entities.values.filter(
								(e) => e.layer === selectedEntity.id
							);
							cesiumViewer.flyTo(findEntity, {
								duration: 0,
								offset: new HeadingPitchRange(0, -90, 0),
							});
						}
						if (selectedEntity.data[0].data.type.match('model')) {
							const primitive = this.getPrimitiveById(entity);

							const center = Matrix4.multiplyByPoint(
								primitive.modelMatrix,
								primitive.boundingSphere.center,
								new Cartesian3()
							);
							const zoomSphere = new BoundingSphere(
								center,
								primitive.boundingSphere.radius
							);
							this.doFly(zoomSphere);

							this.setFocusedModel(entity);
						}
					}
					if (selectedEntity.type === 'file') {
						if (
							!selectedEntity.data[0].data.type.match(
								/model|json/
							)
						) {
							for (
								let i = 0;
								i < cesiumViewer.scene.primitives.length;
								i++
							) {
								if (
									cesiumViewer.scene.primitives.get(i).id ===
									entity
								) {
									this.doFly(
										cesiumViewer.scene.primitives.get(i)
											.boundingSphere
									);
								}
							}
						}
					}
					if (
						selectedEntity.type !== 'layer' &&
						selectedEntity.type !== 'file'
					) {
						const destination = Rectangle.fromCartesianArray([
							this.getEntityBoundingSphere(entity).center,
						]);
						cesiumViewer.camera.setView({ destination });
						cesiumViewer.camera.zoomOut(300);
					}
					if (selectedEntity.type === 'layer') {
						if (selectedEntity.tags.includes('osm')) {
							const geo = JSON.parse(
								JSON.stringify(
									this.$_linearEntities[
										this.getTopLevelParentId(entity)
									].data[0].data.properties.geoJson
								)
							);
							const coords = bbox(geo);
							const destination = Rectangle.fromCartesianArray(
								Cartesian3.fromDegreesArray(coords)
							);
							cesiumViewer.camera.setView({ destination });
							cesiumViewer.camera.zoomOut(300);
						} else if (selectedEntity.tags.includes('imported')) {
							this.doFly(
								this.getEntityBoundingSphere(
									this.getChildren(selectedEntity.id)[0].id
								)
							);
						} else {
							this.zoomHome();
						}
					}
				} catch (error) {
					/*  */
				}
			},
			/**
			 * A hack to get an entity based on its ID if your entity has somehow turned into an object.
			 * @param {String} entity DMP ID of the entity
			 */
			emitEntityClicked(entityId) {
				if (this.$_linearEntities[entityId].type.match('osm')) {
					this.$emit(
						'entity-clicked',
						this.$_linearEntities[
							this.$_linearEntities[entityId].parent
						]
					);
				} else {
					this.$emit(
						'entity-clicked',
						this.$_linearEntities[entityId]
					);
				}
			},
			removeScenePrimitives() {
				return Array.from(
					Array(cesiumViewer.scene.primitives.length),
					(_, index) => index
				)
					.map((x) => cesiumViewer.scene.primitives.get(x))
					.filter((y) =>
						Object.keys(this.$_linearEntities).includes(y.id)
					)
					.map((prim) => prim.remove());
			},
			addBuildings() {
				this.osmBuildings = createOsmBuildings({
					maximumScreenSpaceError: 4,
					defaultColor: new Color(0.84, 0.84, 1, 1),
				});
				this.osmBuildings.style = new Cesium3DTileStyle(
					this.osmBuildingsStyleConditions
				);
				this.showOpenStreetMapsBuildings = this.osmBuildings;
			},
			async getTerrainHeight(position) {
				const positionCartographic = Ellipsoid.WGS84.cartesianToCartographic(
					position
				);
				const latitude = CesiumMath.toDegrees(
					positionCartographic.latitude
				);
				const longitude = CesiumMath.toDegrees(
					positionCartographic.longitude
				);
				const terrainProvider = cesiumViewer.terrainProvider;
				const positions = [
					Cartographic.fromDegrees(longitude, latitude),
				];
				await sampleTerrainMostDetailed(terrainProvider, positions);
				const height = positions[0].height;
				return height;
			},
			getCurrentCameraPostion() {
				const cesiumCamera = cesiumViewer.scene.camera;
				const position = Ellipsoid.WGS84.cartesianToCartographic(
					cesiumCamera.position
				);
				const latitude = CesiumMath.toDegrees(position.latitude);
				const longitude = CesiumMath.toDegrees(position.longitude);
				const height = position.height;
				const heading = CesiumMath.toDegrees(cesiumCamera.heading);
				const pitch = CesiumMath.toDegrees(cesiumCamera.pitch);
				const roll = CesiumMath.toDegrees(cesiumCamera.roll);
				return {
					latitude,
					longitude,
					height,
					heading,
					pitch,
					roll,
				};
			},

			// simplified tour methods
			tourGetCameraPosition() {
				// referenced in CreateTourStop.vue
				this.updateTourViewProperties({
					direction: cesiumViewer.camera.direction.clone(),
					position: cesiumViewer.camera.position.clone(),
					up: cesiumViewer.camera.up.clone(),
				});
			},
			tourGetBasemap() {
				// referenced in CreateTourStop.vue
				// TODO: determine if these properties are needed for tracking basemap/terrain or if others are needed
				this.updateTourViewProperties({
					basemap: JSON.parse(
						JSON.stringify(
							cesiumViewer.baseLayerPicker.viewModel
								.selectedImagery
						)
					),
				});
			},
			flyToStop({ direction, position, up }) {
				cesiumViewer.camera.flyTo({
					destination: position,
					orientation: {
						direction,
						up,
					},
				});
			},
		},
	};
</script>

<style lang="scss">
	#cesium-viewer {
		position: absolute;
		width: 100%;
		height: 100%;
		overflow: hidden;

		.cesium-widget-credits {
			display: none;
		}
		.cesium-viewer-bottom {
			display: none;
		}
	}
</style>
<style>
	.modaltitle {
		display: block;
		width: 100%;
		color: white;
	}
	.utility-box {
		display: block;
		position: absolute;
		top: 0;
		height: 150px;
		width: 100vw;

		background: rgba(51, 51, 51, 0.7);
		color: white;
		z-index: 1999;
	}
	.utility-box-2 {
		display: block;
		position: absolute;
		top: 0;
		height: fit-content;
		width: fit-content;
		margin: 20px;
		padding: 25px;
		background: rgba(51, 51, 51, 0.7);
		color: white;
		z-index: 1999;
	}
</style>
